import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/shop";
import { ProductGrid } from "@/components/site/ProductCard";
import { GridSkeleton, Empty, ErrorState } from "@/components/site/States";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const SORTS = [["newest", "Terbaru"], ["price_asc", "Harga terendah"], ["price_desc", "Harga tertinggi"], ["bestseller", "Terlaris"], ["name", "Nama A-Z"]];

function Filters({ params, set, cats, brands, fixedCat }) {
  const brandSel = (params.get("brand") || "").split(",").filter(Boolean);
  const [min, setMin] = useState(params.get("min_price") || "");
  const [max, setMax] = useState(params.get("max_price") || "");
  const toggleBrand = (b) => set("brand", (brandSel.includes(b) ? brandSel.filter((x) => x !== b) : [...brandSel, b]).join(","));
  const opt = (active) => `w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-neutral-900 text-white font-semibold" : "hover:bg-neutral-100"}`;
  return (
    <div className="space-y-7">
      <div>
        <h3 className="font-heading font-bold mb-2">Ketersediaan</h3>
        {[["", "Semua"], ["ready", "Ready Stock"], ["po", "Pre-Order"]].map(([v, l]) => (
          <button key={v} data-testid={`filter-availability-${v || "all"}`} className={opt((params.get("availability") || "") === v)} onClick={() => set("availability", v)}>{l}</button>
        ))}
      </div>
      {!fixedCat && (
        <div>
          <h3 className="font-heading font-bold mb-2">Kategori</h3>
          <button className={opt(!params.get("category"))} onClick={() => set("category", "")} data-testid="filter-category-all">Semua kategori</button>
          {cats.map((c) => (
            <button key={c.id} data-testid={`filter-category-${c.slug}`} className={opt(params.get("category") === c.slug)} onClick={() => set("category", c.slug)}>{c.name}</button>
          ))}
        </div>
      )}
      <div>
        <h3 className="font-heading font-bold mb-2">Merek</h3>
        <div className="space-y-1 max-h-64 overflow-auto pr-1">
          {brands.map((b) => (
            <label key={b.id} className="flex items-center gap-2 text-sm px-1 py-1 cursor-pointer">
              <input type="checkbox" data-testid={`filter-brand-${b.slug}`} checked={brandSel.includes(b.name)} onChange={() => toggleBrand(b.name)} className="h-4 w-4 accent-[#ff7a00]" />
              {b.name}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading font-bold mb-2">Rentang harga</h3>
        <div className="flex gap-2">
          <input data-testid="filter-min-price" inputMode="numeric" value={min} onChange={(e) => setMin(e.target.value.replace(/\D/g, ""))} placeholder="Min" className="w-full h-10 rounded-lg border px-3 text-sm" />
          <input data-testid="filter-max-price" inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ""))} placeholder="Maks" className="w-full h-10 rounded-lg border px-3 text-sm" />
        </div>
        <button data-testid="filter-price-apply" onClick={() => set({ min_price: min, max_price: max })} className="mt-2 w-full h-10 rounded-lg bg-[var(--bk-yellow)] border-2 border-neutral-900 text-sm font-semibold">Terapkan harga</button>
      </div>
    </div>
  );
}

export default function Catalog() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands").then((r) => r.data) });
  const cat = cats.find((c) => c.slug === slug);
  useSeo(cat ? cat.name : "Semua Produk", cat ? `Koleksi ${cat.name} di Bengkel Toys & Hobbies Pontianak.` : "Katalog lengkap Bengkel Toys & Hobbies.");

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    const obj = typeof k === "object" ? k : { [k]: v };
    Object.entries(obj).forEach(([kk, vv]) => (vv ? n.set(kk, vv) : n.delete(kk)));
    setParams(n);
  };
  const query = { ...Object.fromEntries(params), ...(slug ? { category: slug } : {}) };
  const res = useInfiniteQuery({
    queryKey: ["catalog", query],
    queryFn: ({ pageParam }) => api.get("/products", { params: { ...query, page: pageParam, limit: 20 } }).then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const items = res.data?.pages.flatMap((p) => p.items) || [];
  const total = res.data?.pages[0]?.total ?? 0;
  const activeCount = ["availability", "category", "brand", "min_price", "max_price"].filter((k) => params.get(k)).length;
  const filters = <Filters params={params} set={set} cats={cats} brands={brands} fixedCat={!!slug} />;

  return (
    <div className="container-x py-8" data-testid="catalog-page">
      <nav className="text-xs text-neutral-500 mb-3">
        <Link to="/" className="hover:underline">Beranda</Link> / {slug ? <><Link to="/kategori" className="hover:underline">Kategori</Link> / {cat?.name || slug}</> : "Katalog"}
      </nav>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">{cat ? cat.name : slug ? "Kategori" : "Semua Produk"}</h1>
      {slug && cats.length > 0 && !cat && <p className="text-sm text-red-700 mt-2">Kategori tidak ditemukan.</p>}
      {!slug && <Link to="/koleksi-titipan" className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 bg-[var(--bk-blue)] text-white px-4 py-1.5 text-sm font-bold hover:-translate-y-0.5 transition-transform" data-testid="catalog-collection-link">Lihat Koleksi Titipan & Pre-Owned →</Link>}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <form className="flex-1 min-w-[220px]" onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }}>
          <input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk, merek, atau SKU" className="w-full h-11 rounded-full border-2 border-neutral-900 px-4 text-sm outline-none focus:ring-4 focus:ring-[var(--bk-yellow)]/60" />
        </form>
        <select data-testid="catalog-sort-select" value={params.get("sort") || "newest"} onChange={(e) => set("sort", e.target.value)} className="h-11 rounded-full border-2 border-neutral-900 px-4 text-sm bg-white">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Sheet>
          <SheetTrigger asChild>
            <button data-testid="catalog-filter-button" className="lg:hidden h-11 px-4 rounded-full border-2 border-neutral-900 bg-[var(--bk-yellow)] text-sm font-semibold inline-flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />Filter{activeCount ? ` (${activeCount})` : ""}
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[310px] overflow-auto">
            <SheetTitle className="font-heading mb-4">Filter Produk</SheetTitle>
            {filters}
          </SheetContent>
        </Sheet>
      </div>
      <div className="mt-6 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden lg:block">{filters}</aside>
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-4 text-sm text-neutral-600">
            <span data-testid="catalog-total">{res.isLoading ? "Memuat..." : `${total} produk`}</span>
            {params.get("q") && <span className="inline-flex items-center gap-1 bg-neutral-100 px-2 py-1 rounded-full">"{params.get("q")}"<button onClick={() => set("q", "")} aria-label="Hapus pencarian"><X className="h-3 w-3" /></button></span>}
            {activeCount > 0 && <button data-testid="catalog-reset-filters" onClick={() => setParams({})} className="underline font-semibold">Reset filter</button>}
          </div>
          {res.isLoading ? <GridSkeleton /> : res.isError ? <ErrorState onRetry={res.refetch} /> : items.length ? (
            <>
              <ProductGrid items={items} />
              {res.hasNextPage && (
                <div className="text-center mt-8">
                  <button data-testid="catalog-load-more" onClick={() => res.fetchNextPage()} disabled={res.isFetchingNextPage} className="btn-brand">
                    {res.isFetchingNextPage ? "Memuat..." : "Muat lebih banyak"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <Empty title="Produk tidak ditemukan" text="Coba kata kunci lain atau ubah filter." action={<button onClick={() => setParams({})} className="btn-brand" data-testid="empty-reset">Reset filter</button>} />
          )}
        </div>
      </div>
    </div>
  );
}
